import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, GraduationCap, Users, X, CalendarDays, Pencil, Save, Trash2, Loader2, BookOpen, Upload, RotateCcw } from 'lucide-react';
import { BulkImportModal } from '../ui/BulkImportModal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import api from '../../api';


export function BatchesList({ batches, courses, branches, onUpdateBatches, selectedBatchId, focusedId, onSelect, readOnly, onRefresh }) {
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const getCourse = (id) => courses?.find(c => c.id === id);
  const getBranch = (id) => branches?.find(b => b.id === id);

  const openNew = () => {
    onSelect({ id: 'new', name: '', code: '', course_id: '', branch_id: '', strength: 0, importance: 3, max_events_per_day: 2, course_duration_weeks: 16, course_start_date: '', course_end_date: '' }, 'batch');
  };

  return (
    <>
    <BulkImportModal
      open={bulkImportOpen}
      onOpenChange={setBulkImportOpen}
      entityName="batches"
      onImportComplete={() => { onRefresh?.(); }}
    />
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700/50">
        <div>
          <h2 className="text-base font-bold text-zinc-800 dark:text-zinc-100 tracking-tight">Batches</h2>
          <p className="text-[0.65rem] text-zinc-500 mt-0.5">{batches.length} batches</p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300"
              onClick={() => setBulkImportOpen(true)}
              title="Bulk Import"
            >
              <Upload className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={openNew}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
        )}
      </div>
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-2">
          {batches.map((batch) => {
            const course = getCourse(batch.course_id);
            const branch = getBranch(batch.branch_id);
            return (
              <div key={batch.id} className={cn(
                "data-card !p-3 cursor-pointer transition-all",
                selectedBatchId === batch.id && "ring-2 ring-blue-500/30",
                focusedId === batch.id && "ring-2 ring-violet-500/50 shadow-md"
              )} onClick={() => onSelect(batch, 'batch')} data-card-index={`batch-${batch.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
                      <GraduationCap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">{batch.name}</h4>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500">
                        <Badge variant="secondary" className="text-[0.55rem] h-4">{batch.code}</Badge>
                        {course && <span className="truncate max-w-[80px]">{course.code}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                    <Users className="w-3.5 h-3.5" /><span>{batch.strength}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {batches.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">No batches yet.</p>}
        </div>
      </ScrollArea>
    </div>
    </>
  );
}

export function BatchDetail({ batch, courses, branches, onUpdateBatches, onClose, readOnly }) {
  const [editMode, setEditMode] = useState(batch?.id === 'new');
  const [editForm, setEditForm] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectsError, setSubjectsError] = useState(null);

  const fetchSubjects = async () => {
    setLoadingSubjects(true);
    setSubjectsError(null);
    try {
      if (!batch?.id || batch.id === 'new') { setSubjects([]); return; }
      const { data } = await api.get(`/api/batches/${batch.id}/subjects`);
      setSubjects(data || []);
    } catch (err) {
      console.error('Failed to fetch batch subjects', err);
      const isAuthError = err.response?.status === 401 || err.response?.status === 403;
      setSubjectsError(
        isAuthError 
          ? 'Access denied. You do not have permissions to view subjects for this batch (RLS restriction).' 
          : 'Failed to load subjects. Please check your connection and try again.'
      );
    } finally {
      setLoadingSubjects(false);
    }
  };

  useEffect(() => {
    setSubjectsError(null);
    if (batch?.id && batch.id !== 'new' && !editMode) {
      fetchSubjects();
    } else {
      setSubjects([]);
    }
  }, [batch?.id, batch?.course_id, editMode]);

  useEffect(() => {
    if (batch?.id === 'new') {
      setEditMode(true);
      setEditForm({
        name: '', code: '', course_id: '', branch_id: '', strength: 0, importance: 3, max_events_per_day: 2, course_duration_weeks: 16, course_start_date: '', course_end_date: ''
      });
    } else {
      setEditMode(false);
      setEditForm({});
    }
  }, [batch?.id]);

  const startEdit = () => {
    setEditMode(true);
    setEditForm({
      name: batch.name, code: batch.code,
      course_id: batch.course_id,
      branch_id: batch.branch_id,
      strength: batch.strength || 0, importance: batch.importance || 3,
      max_events_per_day: batch.max_events_per_day || 2,
      course_duration_weeks: batch.course_duration_weeks || 16,
      course_start_date: batch.course_start_date || '',
      course_end_date: batch.course_end_date || '',
    });
  };

  const saveEdit = async () => {
    if (!editForm.name || !editForm.code) { toast.error('Name and code are required'); return; }
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      if (batch.id === 'new') {
        const { data, error } = await supabase.from('batches')
          .insert({ ...editForm, team_id })
          .select()
          .single();
        if (error) throw error;
        onUpdateBatches(prev => [...prev, data]);
        toast.success('Batch created');
        onClose();
      } else {
        const { data, error } = await supabase.from('batches')
          .update(editForm)
          .eq('id', batch.id)
          .eq('team_id', team_id)
          .select()
          .single();
        if (error) throw error;
        onUpdateBatches(prev => prev.map(b => b.id === batch.id ? data : b));
        toast.success('Batch updated');
        setEditMode(false);
      }
    } catch (err) { toast.error(err.message || 'Failed to save batch'); }
  };

  const remove = async () => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { error } = await supabase.from('batches').delete().eq('id', batch.id).eq('team_id', team_id);
      if (error) throw error;
      onUpdateBatches(prev => prev.filter(b => b.id !== batch.id));
      onClose();
      toast.success('Batch deleted');
    } catch (err) { toast.error(err.message || 'Failed to delete batch'); }
  };

  if (!batch) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 animate-slide-in-right">
      <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/50 flex flex-shrink-0 items-center justify-between">
        <h3 className="text-base font-semibold truncate">{batch.id === 'new' ? 'Add Batch' : (editMode ? 'Edit Batch' : batch.name)}</h3>
        <div className="flex flex-shrink-0 items-center gap-1">
          {editMode ? (
            <>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit}>
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              {batch.id !== 'new' && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditMode(false)}>Cancel</Button>
              )}
            </>
          ) : (
            !readOnly && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={startEdit}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )
          )}
          <button className="w-7 h-7 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-400" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-5">
        {editMode ? (
          <div className="space-y-3 mb-6">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name</Label><Input className="mt-1" value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="CS Batch A 2024" /></div>
              <div><Label className="text-xs">Code</Label><Input className="mt-1" value={editForm.code || ''} onChange={e => setEditForm(f => ({ ...f, code: e.target.value }))} placeholder="CSA24" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Course</Label>
                <Select value={editForm.course_id || ''} onValueChange={v => setEditForm(f => ({ ...f, course_id: v }))}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>{(courses || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Branch</Label>
                <Select value={editForm.branch_id || ''} onValueChange={v => setEditForm(f => ({ ...f, branch_id: v }))}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Strength</Label><Input className="mt-1" type="text" inputMode="numeric" pattern="[0-9]*" value={editForm.strength ?? 0} onChange={e => setEditForm(f => ({ ...f, strength: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) }))} /></div>
              <div><Label className="text-xs">Importance (1-5)</Label><Input className="mt-1" type="text" inputMode="numeric" pattern="[0-9]*" value={editForm.importance ?? 3} onChange={e => setEditForm(f => ({ ...f, importance: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) }))} /></div>
              <div><Label className="text-xs">Max Ev/Day</Label><Input className="mt-1" type="text" inputMode="numeric" pattern="[0-9]*" value={editForm.max_events_per_day ?? 2} onChange={e => setEditForm(f => ({ ...f, max_events_per_day: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) }))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Duration (wks)</Label><Input className="mt-1" type="text" inputMode="numeric" pattern="[0-9]*" value={editForm.course_duration_weeks ?? 16} onChange={e => setEditForm(f => ({ ...f, course_duration_weeks: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) }))} /></div>
              <div><Label className="text-xs">Start Date</Label><Input className="mt-1" type="date" value={editForm.course_start_date || ''} onChange={e => setEditForm(f => ({ ...f, course_start_date: e.target.value }))} /></div>
              <div><Label className="text-xs">End Date</Label><Input className="mt-1" type="date" value={editForm.course_end_date || ''} onChange={e => setEditForm(f => ({ ...f, course_end_date: e.target.value }))} /></div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3"><Badge variant="secondary">{batch.code}</Badge></div>
            <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700/50">
              <h4 className="text-xs font-semibold text-zinc-500 uppercase mb-2">Details</h4>
              <p className="text-sm"><span className="text-zinc-500">Branch:</span> <span className="font-medium text-blue-600 dark:text-blue-400">{branches?.find(b => b.id === batch.branch_id)?.name || 'N/A'}</span></p>
              <p className="text-sm"><span className="text-zinc-500">Course:</span> <span className="font-medium text-amber-600 dark:text-amber-400">{courses?.find(c => c.id === batch.course_id)?.name || 'N/A'}</span></p>
              <p className="text-sm mt-2"><span className="text-zinc-500">Strength:</span> <span className="font-medium">{batch.strength} students</span></p>
              <p className="text-sm"><span className="text-zinc-500">Importance:</span> <span className="font-medium">{batch.importance}/5</span></p>
              <p className="text-sm"><span className="text-zinc-500">Max Events/Day:</span> <span className="font-medium">{batch.max_events_per_day ?? 2}</span></p>
            </div>
            {batch.course_duration_weeks && (
              <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700/50">
                <h4 className="text-xs font-semibold text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><CalendarDays className="w-3 h-3" />Course Duration</h4>
                <p className="text-sm"><span className="text-zinc-500">Duration:</span> <span className="font-medium">{batch.course_duration_weeks} weeks</span></p>
                {batch.course_start_date && <p className="text-sm"><span className="text-zinc-500">Start:</span> <span className="font-medium">{batch.course_start_date}</span></p>}
                {batch.course_end_date && <p className="text-sm"><span className="text-zinc-500">End:</span> <span className="font-medium">{batch.course_end_date}</span></p>}
              </div>
            )}

            {batch.id !== 'new' && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5" /> Subject Progress
                  </h4>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-7 text-[0.65rem] border-dashed hover:border-blue-400 hover:text-blue-500" 
                    onClick={fetchSubjects}
                    disabled={loadingSubjects}
                  >
                    <RotateCcw className={cn("w-3 h-3 mr-1", loadingSubjects && "animate-spin")} /> Update Estimates
                  </Button>
                </div>
                <p className="text-[0.65rem] text-zinc-400 mb-4 -mt-3">
                  Calculated by summing all <b>Lecture</b> events assigned to each subject. 
                  Donut indicates <span className="text-emerald-600 dark:text-emerald-400 font-semibold">confirmed</span> (green) vs <span className="text-zinc-500 dark:text-zinc-400 font-semibold">draft</span> (grey) hours.
                </p>

                {loadingSubjects ? (
                  <div className="flex justify-center p-4"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div>
                ) : subjectsError ? (
                  <div className="p-4 text-center border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/10 rounded-lg">
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium mb-2">{subjectsError}</p>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-6 text-[0.65rem] hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/20"
                      onClick={fetchSubjects}
                    >
                      Retry Fetch
                    </Button>
                  </div>
                ) : subjects.length > 0 ? (
                  <div className="space-y-2">
                    {subjects.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700">
                        <div>
                          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{sub.name}</p>
                          <p className="text-xs text-zinc-400">
                            {sub.solid_hours}/{sub.credit_hours} hours scheduled
                            {sub.draft_hours > 0 && (
                              <span className="ml-1 text-[0.68rem] text-zinc-400 dark:text-zinc-500 font-medium">
                                (+{sub.draft_hours}h draft)
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const creditHours = sub.credit_hours || 1;
                            const rawPctSolid = ((sub.solid_hours || 0) / creditHours) * 100;
                            const rawPctDraft = ((sub.draft_hours || 0) / creditHours) * 100;
                            
                            const pctSolid = Math.min(100, Math.max(0, rawPctSolid));
                            const pctDraft = Math.min(100 - pctSolid, Math.max(0, rawPctDraft));

                            return (
                              <div className="relative w-9 h-9 flex items-center justify-center shrink-0">
                                <svg width="36" height="36" viewBox="0 0 36 36" className="absolute inset-0 transform -rotate-90">
                                  {/* Background Track */}
                                  <circle
                                    cx="18"
                                    cy="18"
                                    r="15.915"
                                    fill="transparent"
                                    className="stroke-zinc-100 dark:stroke-zinc-800"
                                    strokeWidth="3.5"
                                  />
                                  {/* Draft (Grey) sector */}
                                  {pctDraft > 0 && (
                                    <circle
                                      cx="18"
                                      cy="18"
                                      r="15.915"
                                      fill="transparent"
                                      className="stroke-zinc-400 dark:stroke-zinc-500"
                                      strokeWidth="3.5"
                                      strokeDasharray={`${pctDraft} 100`}
                                      strokeDashoffset={-pctSolid}
                                    />
                                  )}
                                  {/* Solid (Green) sector */}
                                  {pctSolid > 0 && (
                                    <circle
                                      cx="18"
                                      cy="18"
                                      r="15.915"
                                      fill="transparent"
                                      className="stroke-emerald-500"
                                      strokeWidth="3.5"
                                      strokeDasharray={`${pctSolid} 100`}
                                      strokeDashoffset="0"
                                    />
                                  )}
                                </svg>
                                <span className="text-[8px] font-black text-zinc-700 dark:text-zinc-300 z-10 select-none">
                                  {Math.round(rawPctSolid)}%
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400 p-2 text-center border dashed rounded-md">No subjects found for this batch's course.</p>
                )}
              </div>
            )}
          </div>
        )}
        {!readOnly && batch.id !== 'new' && (
          <Button variant="ghost" size="sm" className="mt-4 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Batch
          </Button>
        )}
      </ScrollArea>
    </div>
  );
}
